import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {forgotPassword, resetPassword} from '@/entities/session/api';
import {messageOf} from '@/shared/lib/errors';
import type {AuthNav} from '@/app/routes';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Screen} from '@/shared/ui/screen';
import {AppText, useLinkText} from '@/shared/ui/text';
import {controls, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Two steps on one screen: ask for the code, then spend it.
 *
 * Splitting them across two routes would let a user back out of step 2 and lose a
 * code that is already in flight to their inbox and expires in fifteen minutes. The
 * step is state, not navigation — and the email carries across for free.
 *
 * The request step never reveals whether the address is known: sv_auth answers
 * "Reset code sent" either way, and so do we. An account-enumeration oracle on a
 * building-access system is not a feature.
 */
const MIN_PASSWORD = 8;

type Step = 'request' | 'reset' | 'done';

const styles = StyleSheet.create({
  body: {justifyContent: 'center', gap: spacing(5), paddingVertical: spacing(8)},
  hero: {alignItems: 'center', gap: spacing(3)},
  heroText: {alignItems: 'center', gap: spacing(1), paddingHorizontal: spacing(4)},
  centered: {textAlign: 'center'},
  form: {gap: spacing(4)},
  link: {minHeight: controls.lg, justifyContent: 'center', alignSelf: 'center'},
});

export function ForgotPasswordScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const link = useLinkText();
  const nav = useNavigation<AuthNav>();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestCode() {
    const mail = email.trim();
    if (!mail) {
      setError(t('auth.errors.emailRequired'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(mail);
      setStep('reset');
    } catch (e) {
      setError(messageOf(e, t('errors.network')));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset() {
    if (!code.trim()) {
      setError(t('auth.errors.codeRequired'));
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(t('auth.errors.passwordTooShort'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await resetPassword(email.trim(), code.trim(), password);
      setStep('done');
    } catch (e) {
      setError(messageOf(e, t('errors.network')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen scroll width="narrow" style={styles.body} testID="forgot-screen">
      <View style={styles.hero}>
        {/* Recovery keeps a glyph rather than the wordmark: the state of the flow
            (a lock, then a tick) is what the user needs here, and the header of the
            auth stack already established which product they are in. */}
        <IconBubble tone={p.accent} size={56} square>
          <Icon name={step === 'done' ? 'check' : 'lock'} size={28} color={p.primary} />
        </IconBubble>
        <View style={styles.heroText}>
          <AppText variant="display" style={styles.centered}>
            {t('auth.forgot.title')}
          </AppText>
          <AppText variant="body" color={p.mutedForeground} style={styles.centered}>
            {step === 'request'
              ? t('auth.forgot.requestSubtitle')
              : t('auth.forgot.resetSubtitle')}
          </AppText>
        </View>
      </View>

      <Card padding={5} style={styles.form}>
        {step === 'request' ? (
          <>
            <AppInput
              testID="forgot-email"
              label={t('auth.forgot.email')}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              icon="mail"
              onSubmitEditing={requestCode}
              returnKeyType="go"
            />
            <AppButton
              testID="forgot-request"
              title={t('auth.forgot.requestSubmit')}
              size="lg"
              onPress={requestCode}
              loading={busy}
            />
          </>
        ) : null}

        {step === 'reset' ? (
          <>
            {/* Which inbox to go and look in — the one thing the user needs while
                they switch apps, so it stays on screen rather than flashing past. */}
            <Banner
              tone="info"
              icon="mail"
              title={t('auth.forgot.codeSent', {email: email.trim()})}
            />
            <AppInput
              testID="forgot-code"
              label={t('auth.forgot.code')}
              placeholder={t('auth.forgot.codePlaceholder')}
              value={code}
              onChangeText={setCode}
              keyboardType="number-pad"
              maxLength={6}
            />
            <AppInput
              testID="forgot-password"
              label={t('auth.forgot.newPassword')}
              value={password}
              onChangeText={setPassword}
              secure
              autoCapitalize="none"
              icon="lock"
              showPasswordLabel={t('a11y.showPassword')}
              hidePasswordLabel={t('a11y.hidePassword')}
            />
            <AppButton
              testID="forgot-reset"
              title={t('auth.forgot.resetSubmit')}
              size="lg"
              onPress={submitReset}
              loading={busy}
            />
          </>
        ) : null}

        {step === 'done' ? (
          <Banner tone="success" title={t('auth.forgot.done')} />
        ) : null}

        {error ? (
          <Banner testID="forgot-error" tone="error" title={error} />
        ) : null}

        <Pressable
          testID="forgot-to-login"
          style={styles.link}
          accessibilityRole="link"
          onPress={() => nav.navigate('Login')}>
          <AppText variant="label" style={link}>
            {t('auth.forgot.toLogin')}
          </AppText>
        </Pressable>
      </Card>
    </Screen>
  );
}
